import { useCallback, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
} from "@mui/material";
import { TDocumentTemplate } from "../../models";
import { filterInput, numbersRegExp } from "../../../../utils/validate";
import { ActionType } from "../../../../models/ActionType";
import { useCreateDocumentTemplate } from "../../hooks/documentTemplates/useCreateDocumentTemplate";
import {
  addDocumentTemplateVersion,
  generateDocumentTemplateUploadUrl,
} from "../../network";
import { uploadToS3 } from "../../../company/network";
import { FileInput } from "../../../ui/components/FileInput";
import { ConfirmationDialog } from "../../../ui/components/ConfirmationDialog";
import { mainTheme } from "../../../../theme/mainTheme";
import { useParams } from "react-router-dom";

export type TProps = {
  onClose: (success: boolean) => void;
  actionType: ActionType;
  selectedTemplate: TDocumentTemplate | null;
  open: boolean;
};

export const TemplateDialog = (props: TProps) => {
  const { onClose, open, selectedTemplate, actionType } = props;
  const { id } = useParams();

  const [
    actionTemplateResult,
    actionTemplateLoading,
    actionTemplateError,
    actionTemplate,
  ] = useCreateDocumentTemplate(actionType, id!);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState("1");
  const [code, setCode] = useState("");
  const [document, setDocument] = useState<File | string | null>(null);
  const [showFilePicker, setShowFilePicker] = useState(false);

  const handleClear = () => {
    setName("");
    setDescription("");
    setIsActive(true);
    setSortOrder("1");
    setCode("");
    setDocument(null);
    setShowFilePicker(false);
  };

  useEffect(() => {
    if (open) {
      if (selectedTemplate) {
        setName(selectedTemplate.name);
        setDescription(selectedTemplate.description);
        setIsActive(selectedTemplate.isActive);
        setSortOrder(String(selectedTemplate.sortOrder));
        setCode(selectedTemplate.code);
      }
      setDocument(null);
      setShowFilePicker(false);
    }
  }, [selectedTemplate, open]);

  const handleDocumentChange = (file: File | string | null) => {
    setDocument(file);
    if (!file && selectedTemplate) {
      setShowFilePicker(false);
    }
  };

  const handleSubmit = async () => {
    let template_id = "";
    if (!selectedTemplate) {
      const { id } = await actionTemplate({
        name,
        description,
        is_active: isActive,
        sort_order: Number(sortOrder),
        code: code,
      });
      template_id = id;
    } else {
      await actionTemplate({
        id: selectedTemplate.id,
        name,
        description,
        is_active: isActive,
        sort_order: Number(sortOrder),
        code: code,
      });
      template_id = selectedTemplate.id;
    }
    if (document) {
      if (typeof document === "object") {
        try {
          const { filePath, uploadUrl } =
            await generateDocumentTemplateUploadUrl(template_id);
          await uploadToS3(uploadUrl, document, document.type);
          addDocumentTemplateVersion(template_id, "", filePath);
        } catch (e) {
          console.error(e);
        }
      }
    }
  };

  const handleCancel = useCallback(
    (success: boolean) => {
      handleClear();
      onClose(success);
    },
    [onClose]
  );

  useEffect(() => {
    if (actionTemplateResult) {
      handleCancel(true);
    }
  }, [actionTemplateResult, handleCancel]);

  if (actionType === ActionType.DELETE) {
    return (
      <ConfirmationDialog
        title='Are you sure?'
        description={`Are you sure to delete template ${name}`}
        error={actionTemplateError}
        confirmText='Delete'
        confirmAction={() => actionTemplate(selectedTemplate!.id)}
        disabled={actionTemplateLoading}
        onClose={() => handleCancel(false)}
        isOpen={open}
        confirmButtonColor='error'
        titleColor={mainTheme.palette.error.main}
      />
    );
  }

  return (
    <Dialog
      sx={{ "& .MuiDialog-paper": { width: "80%" } }}
      maxWidth='xs'
      open={open}
      id='create-app-dialog'
      keepMounted
    >
      <DialogTitle sx={{ textTransform: "capitalize" }}>
        {actionType} template
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <FormControlLabel
            control={<Checkbox />}
            label={"Active"}
            name='isActive'
            checked={isActive}
            onChange={() => setIsActive((prev) => !prev)}
          />
          <TextField
            label='Name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            size='small'
          />
          <TextField
            label='Description'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size='small'
          />
          <TextField
            label='Sort order'
            value={sortOrder}
            onChange={(e) =>
              filterInput(
                (e.target as HTMLTextAreaElement).value,
                numbersRegExp,
                setSortOrder
              )
            }
            size='small'
          />
          <TextField
            label='Code'
            value={code}
            onChange={(e) => setCode(e.target.value)}
            size='small'
          />
          {selectedTemplate && !showFilePicker && !document ? (
            <Box>
              <Button
                onClick={() => setShowFilePicker(true)}
                sx={{
                  color: mainTheme.palette.primary.main,
                  textTransform: "none",
                  padding: 0,
                  justifyContent: "flex-start",
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                + Change document
              </Button>
            </Box>
          ) : (
            <FileInput
              accept='.doc, .docx'
              label='Document'
              namePrefix='document'
              handleChange={handleDocumentChange}
              value={document}
              hideUploadedStatus={true}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={() => handleCancel(false)}>
          Cancel
        </Button>
        <Button onClick={handleSubmit}>Submit</Button>
      </DialogActions>
    </Dialog>
  );
};
